import {
  AfterViewInit,
  Component,
  ComponentRef,
  createEnvironmentInjector,
  ElementRef,
  EnvironmentInjector,
  EventEmitter,
  Injector,
  OnInit,
  Output,
  Type,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ActiveConfig,
  ComponentLabel,
  SupportedDevice,
  WorkflowConfig,
  WorkflowWidgetConfig,
  WorkflowZoneDevicesConfig,
} from '@finxone-platform/shared/sys-config-types';
import { combineLatest, Observable, of, Subject, take, takeUntil, tap } from 'rxjs';

import { ActivatedRoute, ParamMap } from '@angular/router';

import { ParentContainerComponent } from '@app/finxone-web-frontend/app/Modules/zones/parent-container/parent-container.component';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';

import { ImagePopupSettingConfig } from '@app/finxone-web-frontend/app/lib/utils/widget.utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { ClearContainerAndConfig } from '../libs/actions/app-grid.action';
import { ActiveConfigService } from '../libs/services/active-config/active-config.service';
import { AppBuilderAdapterService } from '../libs/services/app-builder/app-builder-adapter.service';
import { RouteService } from '../libs/services/route-service/route.service';
import { SystemConfigService } from '../libs/services/system-config/system-config.service';
import { WorkflowConfigService } from '../libs/services/workflow-config/workflow-config.service';
import { AppGridState } from '../libs/state/app-grid.state';
import { ProjectsState } from '../libs/state/project.state';
import { createMockEnvironmentProviders } from '../libs/utils/mock-environment.provider';
import { screenTypes } from '../modules/ui-builder/device-holder/device-holder.component';
import { ApiService } from '../libs/services/api-service/api.service';
import { StateService } from '@app/finxone-web-frontend/app/lib/services/state-service/state.service';

@Component({
  standalone: false,
  selector: 'app-preview-device-holder',
  templateUrl: './preview-device-holder.component.html',
  styleUrls: ['./preview-device-holder.component.scss'],
  // This is done to not be bound by body styling applied to workflow app that shows a scrollbar
  // Be warned
  encapsulation: ViewEncapsulation.None,
})
export class PreviewDeviceHolderComponent implements OnInit, AfterViewInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public device: SupportedDevice = 'mobile';

  @ViewChild('element', { static: true, read: ElementRef })
  element: ElementRef<HTMLElement>;
  // add type annotation to ViewChild decorator
  @ViewChild('container', { read: ViewContainerRef, static: true })
  container!: ViewContainerRef;
  @Output() showImagePopup = new EventEmitter<ImagePopupSettingConfig>();
  // add type annotations to class properties
  private newEnvInjector: EnvironmentInjector;
  private prevZone = '';
  private workflowConfig!: WorkflowConfig;
  private activeConfig!: ActiveConfig;
  private widgets!: WorkflowWidgetConfig[];
  private parentComponentRef!: ComponentRef<any>;
  private currentZone: any;

  private prevParentComponentName: string | null = null;

  @Select(AppGridState.getVisibleCanvasComponent)
  private visibleCanvasComponent!: Observable<{
    component: ComponentLabel;
    zone?: string;
    pageIndexOrSectionName?: number | string;
  }>;

  constructor(
    private systemConfigService: SystemConfigService,
    private injector: EnvironmentInjector,
    private appBuilderService: AppBuilderAdapterService,
    private workflowConfigService: WorkflowConfigService,
    private activeConfigService: ActiveConfigService,
    private store: Store,
    private actions$: Actions,
    private registry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private routeService: RouteService,
    private apiService: ApiService,
    private stateService: StateService,
  ) {
    document.body.id = 'web-app';
    this.activeConfigService.isAppWindowPreview = true;
    this.device = this.activeConfigService.activeState.activeDevice;

    this.newEnvInjector = createEnvironmentInjector(
      createMockEnvironmentProviders(
        this.systemConfigService,
        this.store,
        this.registry,
        this.domSanitizer,
        this.routeService,
        this.activeConfigService,
        this.apiService,
        this.stateService,
      ),
      this.injector,
    );
  }

  ngOnInit() {
    combineLatest([this.activeConfigService.getConfig(), this.systemConfigService.getConfig()]).subscribe(
      ([config]) => {
        this.activeConfig = config;
        this.currentZone = config.activeZone;
      },
    );

    this.actions$
      .pipe(ofActionDispatched(ClearContainerAndConfig), takeUntil(this.destroy$))
      .subscribe(() => {
        this.container.clear();
      });

    //Load active components on page refresh
    this.refreshWorkflowConfig();

    this.visibleCanvasComponent
      .pipe(takeUntil(this.destroy$))
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .subscribe(({ component }) => {
        const parentComponentChanged = this.prevParentComponentName !== component;

        const moduleChanged = this.prevZone != this.currentZone || this.container.length == 0;

        const allwidgets: WorkflowWidgetConfig[] = this.appBuilderService.getCurrentPageWidgets();
        this.widgets = allwidgets;
        if (moduleChanged || parentComponentChanged) {
          this.createParentComponent(component);
        }
        this.prevZone = this.currentZone;
        this.prevParentComponentName = component;
      });

    this.onGetLayoutPositionUpdates();
  }

  private getZoneAndPageUrl(): ParamMap {
    const role = this.workflowConfig.permissionSections.roles.find(
      (role) => role.name === this.activeConfig.activeRole,
    );

    if (role) {
      const config = (
        role.zones[this.activeConfig.activeZone]?.canvases.subscription[
          this.activeConfig.activeCanvasSubscription
        ] as WorkflowZoneDevicesConfig
      )[this.activeConfig.activeDevice];

      if (config) {
        return new Map<string, string>([
          ['zone', formatNameAsUrl(this.activeConfig.activeZone)],
          ['page', config.pages[this.activeConfig.activePageIndex]?.pageConfiguration?.url ?? ''],
        ]) as unknown as ParamMap;
      }
    }
    throw new Error('zone and page is invalid and not present');
  }

  private onGetLayoutPositionUpdates(): void {
    const page = this.parentComponentRef?.instance;
    if (page) page.envInjector = this.newEnvInjector;
  }

  createParentComponent(componentName: string): void {
    this.container.clear();
    this.widgets = [];

    this.parentComponentRef = this.container.createComponent(
      this.getComponentBasedOnMainComponentPageName(componentName),
      {
        injector: Injector.create({
          providers: [
            {
              provide: ActivatedRoute,
              useValue: {
                paramMap: of(this.getZoneAndPageUrl()),
              },
            },
          ],
        }),
        environmentInjector: this.newEnvInjector,
        index: this.widgets.length,
      },
    );
    if (componentName === 'ParentContainerComponent') {
      this.parentComponentRef.setInput('device', this.device);
      this.parentComponentRef.setInput('paymentProviderConfig', {
        payment: this.activeProject.paymentGateway,
        sandboxMode: this.activeProject?.sandboxMode,
      });
    }
  }

  private get activeProject() {
    return this.store.selectSnapshot(ProjectsState.getActiveProject);
  }

  ngAfterViewInit(): void {
    this.onGetSelectedDevice(screenTypes[this.device]);
  }

  refreshWorkflowConfig() {
    this.workflowConfigService
      .getConfig()
      .pipe(
        take(1),
        tap((config: WorkflowConfig) => {
          this.workflowConfig = config;
        }),
      )
      .subscribe();
  }

  onGetSelectedDevice(event: { width: number; height: number; deviceClass: string }) {
    this.activeConfigService.updateConfig((activeState) => {
      activeState.activeScreenWidth = event.width;
      activeState.activeScreenHeight = event.height;
      return activeState;
    });
  }

  private getComponentBasedOnMainComponentPageName(mainComponentPageName: string): Type<any> {
    switch (mainComponentPageName) {
      case 'IndividualAccountPageComponent':
        return ParentContainerComponent;
      case 'ParentContainerComponent':
        return ParentContainerComponent;
      case 'ChartWidgetIntegrationComponent':
        return ParentContainerComponent;
      case 'GenralAccountPageComponent':
        return ParentContainerComponent;
      case 'MenuComponent':
        return ParentContainerComponent;
      case 'CardManagementZonePageComponent':
        return ParentContainerComponent;
      default:
        throw new Error(
          `Invalid mainComponentPageName provided no components in getComponentBasedOnMainComponentPageName() match: ${mainComponentPageName}`,
        );
    }
  }
}
